import { PROGRAM_TYPE, REACT_QUERY_KEYS } from '@intigral/constant';
import { Button } from '@intigral/shared/components/Button';
import { FontIcon } from '@intigral/shared/components/FontIcon';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ButtonTypes, COMPONENT_TYPES, ITEM_TYPE } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import {
    getItemProperties,
    getMixpanelLinearProperties,
    getMixpanelVodProperties,
} from '../../contexts/MixpanelHelper';
import h from '../../helpers/common';
import url from '../../helpers/url';
import user from '../../helpers/user';
import useCatchupDetailOverlay from '../../hooks/useCatchupDetailOverlay';
import useDetailOverlay from '../../hooks/useDetailOverlay';
import useToggleDetailOverlay from '../../hooks/useToggleDetailOverlay';
import usePPVManagement from '../../hooks/usePPVManagement';
import { useTvod } from '../../hooks/useTvod';
import { isSubscriptionLockedContent, showUpgradeOrOpenDetail } from '../../helpers/vod';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { IMixpanelContext } from './moods/interface';

interface DetailsButtonProps {
    children?: React.ReactNode;
    className?: string;
    buttonType?: any;
    item?: any;
    componentType?: string;
    componentName?: string;
    meta?: any;
    notClickable?: boolean;
    onClick?: () => void;
}

const DetailsButton: React.FC<DetailsButtonProps> = ({
    children,
    className = '',
    buttonType,
    item,
    componentType,
    componentName,
    meta,
    notClickable = false,
    onClick = () => {
        return;
    },
}) => {
    const queryClient = useQueryClient();
    const queryKeyItemId = item.programType === PROGRAM_TYPE.MOVIE ? item.Id : item.guid;
    queryClient.setQueryData([REACT_QUERY_KEYS.VOD_DETAILS, queryKeyItemId], (old) => {
        if (old === undefined || old === null) return item;
        return undefined;
    });

    const {
        track = () => {
            return;
        },
    } = useContext<IMixpanelContext>(MixpanelContext) || {};

    const openDetailOverlay = useDetailOverlay();
    const { showPpvOverlay } = useToggleDetailOverlay();
    const { openCatchupDetailOverlay } = useCatchupDetailOverlay();
    const dispatch = useDispatch();
    const { shouldShowLock } = usePPVManagement();
    const { isTvod } = useTvod(item);
    const channelId = item?.channelId || item?.channelID || item?.stationId;
    const { isLocked } = shouldShowLock(item, channelId) || {};
    const locked = (!isTvod && isLocked) || item?.isLocked;

    const openDetailWithLockCheck = () => {
        showUpgradeOrOpenDetail({
            item,
            locked,
            dispatch,
            openDetail: () => openDetailOverlay(item),
        });
    };

    const openCatchupWithLockCheck = () => {
        if (isSubscriptionLockedContent(item, locked)) {
            dispatch(showUpgradedModal(true));
            return;
        }
        openCatchupDetailOverlay({
            channelGuid: item.channelGuid,
            paId: item.paId,
            stationCallSign: item?.channelGuid?.split('_')?.[0] || '',
        });
    };

    const handleMixpanelEvents = () => {
        const eventProps = {
            [KEYS.CAROUSEL.SCREEN_NAME]: h.getScreen(),
            [KEYS.CAROUSEL.CAROUSEL_NAME]: componentName,
            [KEYS.CAROUSEL.CAROUSEL_TYPE]: h.getCarouselComponentType(componentType),
        };
        switch (componentType) {
            case COMPONENT_TYPES.FEATURED_SLIDER:
                track(`${h.getScreen()} - ${EVENT.PROMOTIONS.INFO}`, getItemProperties(item));
                break;
            case COMPONENT_TYPES.VOD_CAROUSEL:
                if (componentName === COMPONENT_TYPES.SEARCH_RECOMMENDATIONS) {
                    track(`${EVENT.SEARCH_RESULTS.RECOMMENDATION_ITEM_CLICK}`, {
                        [KEYS.GENERAL.SEARCH_RECOMMENDATION_TYPE]:
                            meta && meta.title && meta.title.en ? meta.title.en : '',
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                } else {
                    track(`${EVENT.CAROUSEL.ITEM_CLICK}`, {
                        [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                }
                break;
            case COMPONENT_TYPES.VOD_FEATURED_CAROUSEL:
                track(`${EVENT.CAROUSEL.ITEM_CLICK}`, {
                    [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.USER_WATCHLIST:
                track(`${EVENT.WATCHLIST.ITEM_CLICK}`, {
                    [KEYS.GENERAL.ITEM_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.SEARCH_RESULTS:
                track(`${EVENT.SEARCH_RESULTS.ITEM_CLICK}`, {
                    ...(item.programType && { [KEYS.GENERAL.ITEM_TYPE]: ITEM_TYPE[item.programType.toUpperCase()] }),
                    ...getItemProperties(item),
                });
                break;
            case COMPONENT_TYPES.FULLVOD_CATALOG_LIST:
                track(`${EVENT.VOD_LISTING.ITEM_CLICK}`, { ...getMixpanelVodProperties(item), ...eventProps });
                break;
            case COMPONENT_TYPES.CONTINIE_WATCHING:
                track(`${EVENT.CONTINUE_WATCHING.ITEM_CLICK}`, {
                    [KEYS.GENERAL.PROGRAM_TYPE]: item.programType,
                    ...(item.programType === 'VOD'
                        ? getMixpanelVodProperties(item)
                        : getMixpanelLinearProperties(item)),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.WATCHLIST_CAROUSEL:
                track(`${EVENT.WATCHLIST_CAROUSEL.ITEM_CLICK}`, {
                    [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.IMAGES_CAROUSEL:
                track(`${EVENT.IMAGES_CAROUSEL.IMAGE_CLICK}`, {
                    [KEYS.GENERAL.WATCHLIST_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            default:
                break;
        }
    };

    const onclickHandler = () => {
        onClick();
        handleMixpanelEvents();
    };

    if (!item) {
        return null;
    }
    if (notClickable) {
        return <div>{children}</div>;
    }

    const pro_type = PROGRAM_TYPE[item?.programType?.toUpperCase()];
    switch (buttonType) {
        case ButtonTypes.VodDetails:
            return (
                <Link
                    to={url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                    state={item}
                    className={className}
                    onClick={onclickHandler}
                >
                    {children}
                </Link>
            );
        case ButtonTypes.VodFeaturedItem:
            if (item && item.promoType && ['SUBSCRIPTION', 'TIER'].includes(item.promoType)) {
                const redirectLink = user.getUserProfileUrl();
                return (
                    <Button social secondary onClick={onclickHandler} LinkComponent="a" href={redirectLink}>
                        {children}
                        <FontIcon name="more" size="smaller" md="smaller"></FontIcon>
                    </Button>
                );
            } else {
                return (
                    <Button
                        social
                        secondary
                        onClick={() => {
                            if (item.promoType === 'PPV') {
                                showPpvOverlay({
                                    eventId: item?.ppvEvent?.eventId,
                                    type: 'Home_Screen',
                                });
                            } else if (item.promoType === 'CATCHUP') {
                                openCatchupWithLockCheck();
                            } else {
                                openDetailWithLockCheck();
                            }
                            onclickHandler();
                        }}
                    >
                        {children}
                        <FontIcon name="more" size="smaller" md="smaller"></FontIcon>
                    </Button>
                );
            }
        case ButtonTypes.EpisodeItem:
            if (componentType === COMPONENT_TYPES.SEARCH_RESULTS) {
                return (
                    <Link
                        className={className}
                        onClick={() => {
                            openDetailWithLockCheck();
                            onclickHandler();
                        }}
                        to={{}}
                    >
                        {children}
                    </Link>
                );
            } else {
                return (
                    <Link
                        to={url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                        state={item}
                        className={className}
                        onClick={onclickHandler}
                    >
                        {children}
                    </Link>
                );
            }
        case ButtonTypes.RewindItem:
            return (
                <Link
                    to={url.route(url.getDetailsPage('live'), {
                        id: btoa(item.Id),
                        stationId: btoa(item.stationId),
                        startDate: btoa(item?.startTime),
                    })}
                    className={className}
                    onClick={onclickHandler}
                >
                    {children}
                </Link>
            );
        case ButtonTypes.ContinueWatching:
            return item.programType !== 'LINEAR' ? (
                <span
                    className={className}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openDetailWithLockCheck();
                        onclickHandler();
                    }}
                >
                    {children}
                </span>
            ) : (
                <>{children}</>
            );
        case ButtonTypes.WatchlistCarouselItem:
            return item.programType !== 'Rewind' && pro_type !== PROGRAM_TYPE.EPISODE ? (
                <span
                    className={className}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openDetailWithLockCheck();
                        onclickHandler();
                    }}
                >
                    {children}
                </span>
            ) : (
                <>{children}</>
            );
        case ButtonTypes.ModalItem:
            return <div onClick={onclickHandler}>{children}</div>;

        case ButtonTypes.VodeItem:
            // TODO: not needed?
            return (
                <Button
                    compact
                    social
                    secondary
                    onClick={() => {
                        openDetailWithLockCheck();
                        onclickHandler();
                    }}
                >
                    <FontIcon name="info" size="smaller" md="smaller"></FontIcon>
                    {children}
                </Button>
            );
        case 'VodItem-details-overlay':
            // TODO: rename to ButtonTypes.VodeItem and remove `case ButtonTypes.VodeItem:`
            return (
                <Button
                    social
                    secondary
                    compact
                    onClick={() => {
                        openDetailWithLockCheck();
                        onclickHandler();
                    }}
                >
                    <FontIcon name="info" size="medium" md="large" />
                    {children}
                </Button>
            );
        default:
            return (
                <a
                    onClick={() => {
                        openDetailWithLockCheck();
                        onclickHandler();
                    }}
                    // to={url.route(url.getDetailsPage(item.programType), { id: btoa(item.Id), guid: btoa(item.guid) })}
                    className={className}
                >
                    {children}
                </a>
            );
    }
};

export default DetailsButton;
